import chevronLeft from "assets/images/v2/chevron-left.svg";
import Drawer, { DrawerType } from "components/ui/drawer/Drawer";
import { Button } from "components/v2/button/Button";
import DocumentPreview from "components/v2/document-preview/DocumentPreview";
import { Spinner } from "components/v2/spinner/spinner";
import {
  getCurrentUserId,
  getNhDealApplicationId,
} from "features/authSlice";
import useDesktopChecker from "hooks/useDesktopChecker";
import useTabletChecker from "hooks/useTabletChecker";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { base64ToUint8Array } from "utilities/file";
import {
  WIRE_FRAUD_COMPLETED_DESCRIPTION,
  WIRE_FRAUD_DETAIL_DESCRIPTION,
  WIRE_FRAUD_DOCUMENT_LOAD_ERROR,
  WIRE_FRAUD_DOCUMENT_TITLE,
  WIRE_FRAUD_TASK_TITLE,
} from "utilities/wireFraud";
import {
  useCompleteWireFraudTaskMutation,
  useLazyRetrieveWireFraudDocumentQuery,
} from "../../dashboardService";
import "./WireFraudPanel.scss";

interface WireFraudPanelProps {
  readonly setShowUpload: (show: boolean) => void;
  readonly onAcknowledged?: () => Promise<void> | void;
  readonly isCompleted?: boolean;
}

const WireFraudPanel = ({
  setShowUpload,
  onAcknowledged,
  isCompleted = false,
}: WireFraudPanelProps) => {
  const isDesktop = useDesktopChecker();
  const isTablet = useTabletChecker();
  const nhDealApplicationId = useSelector(getNhDealApplicationId);
  const userId = useSelector(getCurrentUserId);
  const [retrieveWireFraudDocument, { isFetching }] =
    useLazyRetrieveWireFraudDocumentQuery();
  const [completeWireFraudTask] = useCompleteWireFraudTaskMutation();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [fileUrl, setFileUrl] = useState("");
  const [fileName, setFileName] = useState(`${WIRE_FRAUD_DOCUMENT_TITLE}.pdf`);
  const fileUrlRef = useRef("");
  const completionInProgressRef = useRef(false);
  const hasAcknowledgedRef = useRef(false);

  const releaseFileUrl = useCallback(() => {
    if (fileUrlRef.current) {
      globalThis.URL.revokeObjectURL(fileUrlRef.current);
      fileUrlRef.current = "";
    }
    setFileUrl("");
  }, []);

  useEffect(
    () => () => {
      if (fileUrlRef.current) {
        globalThis.URL.revokeObjectURL(fileUrlRef.current);
      }
    },
    [],
  );

  const showDocument = (fileContents: string, contentType: string, name?: string) => {
    const blob = new Blob([base64ToUint8Array(fileContents)], { type: contentType });
    const objectUrl = globalThis.URL.createObjectURL(blob);
    fileUrlRef.current = objectUrl;
    setFileName(name ?? `${WIRE_FRAUD_DOCUMENT_TITLE}.pdf`);
    setFileUrl(objectUrl);
  };

  const handleReviewDocument = async () => {
    setLoadError("");

    if (!nhDealApplicationId || !userId) {
      setLoadError(WIRE_FRAUD_DOCUMENT_LOAD_ERROR);
      return;
    }

    setIsDrawerOpen(true);
    releaseFileUrl();

    try {
      const document = await retrieveWireFraudDocument({
        nhDealApplicationId,
        userId,
      }).unwrap();
      showDocument(document.fileContents, document.contentType, document.name);
    } catch {
      setIsDrawerOpen(false);
      setLoadError(WIRE_FRAUD_DOCUMENT_LOAD_ERROR);
    }
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    releaseFileUrl();
  };

  const handleCloseDrawer = async () => {
    if (isCompleted || !fileUrl || isFetching || hasAcknowledgedRef.current) {
      closeDrawer();
      return;
    }

    if (completionInProgressRef.current || !nhDealApplicationId || !userId) {
      return;
    }

    completionInProgressRef.current = true;
    setIsCompleting(true);

    try {
      await completeWireFraudTask({ nhDealApplicationId, userId }).unwrap();
      hasAcknowledgedRef.current = true;
      closeDrawer();
      await onAcknowledged?.();
    } catch {
      return;
    } finally {
      completionInProgressRef.current = false;
      setIsCompleting(false);
    }
  };

  const handleDownload = () => {
    if (!fileUrl) {
      return;
    }

    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = fileName;
    link.click();
  };

  return (
    <section className="wire-fraud-panel">
      {!isDesktop && !isTablet && (
        <button
          className="wire-fraud-panel__back"
          onClick={() => setShowUpload(false)}
          type="button"
        >
          <img src={chevronLeft} alt="" />
          <span>Back to tasks</span>
        </button>
      )}

      <div className="wire-fraud-panel__content">
        <h5 className="wire-fraud-panel__title paragraph-medium-medium">
          {WIRE_FRAUD_TASK_TITLE}
        </h5>
        <p className="wire-fraud-panel__description paragraph-medium-regular">
          {isCompleted ? WIRE_FRAUD_COMPLETED_DESCRIPTION : WIRE_FRAUD_DETAIL_DESCRIPTION}
        </p>
        <Button
          label={isCompleted ? "View Document" : "Review Document"}
          onClick={() => void handleReviewDocument()}
          disabled={isFetching}
          className="wire-fraud-panel__review-button"
        />
        {loadError && (
          <p className="wire-fraud-panel__error" role="alert">
            {loadError}
          </p>
        )}
      </div>

      <Drawer
        isOpen={isDrawerOpen}
        onClose={() => void handleCloseDrawer()}
        title={WIRE_FRAUD_DOCUMENT_TITLE}
        customCss="preview-document"
        type={DrawerType.DocumentPreview}
        centerContent={isFetching}
        footerText={isCompleted ? undefined : WIRE_FRAUD_DETAIL_DESCRIPTION}
        closeText={isCompleted ? "Close" : "Done"}
        closeDisabled={isCompleting}
        footerActionDisabled={isFetching || isCompleting || !fileUrl}
      >
        {isFetching && <Spinner isApi />}
        {!isFetching && fileUrl && (
          <DocumentPreview
            fileUrl={fileUrl}
            fileName={fileName}
            handleDownload={handleDownload}
          />
        )}
      </Drawer>
    </section>
  );
};

export default WireFraudPanel;
